import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Divider,
  CircularProgress,
  ListItemButton,
} from '@mui/material';
import { History, Person, Business, Group, Public, Category, Article, LocalOffer } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { SearchService } from 'figshelf-modules';
import Icon from './Icon';
import { CONTEXTS } from 'figshelf-modules';

const SearchDropdown = ({ searchQuery, onClose, anchorEl }) => {
  const navigate = useNavigate();
  const auth = useContext(CONTEXTS.AuthContext);
  const [results, setResults] = useState({
    users: [],
    companies: [],
    figures: [],
    groups: [],
    posts: [],
    meta: null,
  });
  const [resultCounts, setResultCounts] = useState({
    users: 0,
    companies: 0,
    figures: 0,
    groups: 0,
    posts: 0,
  });
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      performSearch(searchQuery);
    } else {
      if (auth?.isSignedIn) {
        loadRecentSearches();
      } else {
        setRecentSearches([]);
      }
    }
  }, [searchQuery, auth?.isSignedIn]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          anchorEl && !anchorEl.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [anchorEl, onClose]);

  const performSearch = async (query) => {
    try {
      setLoading(true);
      // Get both limited results for dropdown and full counts
      const [searchResults, fullResults] = await Promise.all([
        SearchService.search(query, 5, 0), // Limited results for display
        SearchService.search(query, 100, 0) // Get more to calculate accurate counts
      ]);

      console.log('Search results:', searchResults);
      setResults(searchResults);
      
      // Set counts from full results
      setResultCounts({
        users: fullResults?.users?.length || 0,
        companies: fullResults?.companies?.length || 0,
        figures: fullResults?.figures?.length || 0,
        groups: fullResults?.groups?.length || 0,
        posts: fullResults?.posts?.length || 0,
      });
    } catch (error) {
      console.error('Search error:', error);
      setResults({
        users: [],
        companies: [],
        figures: [],
        groups: [],
        posts: [],
      });
      setResultCounts({
        users: 0,
        companies: 0,
        figures: 0,
        groups: 0,
        posts: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadRecentSearches = async () => {
    try {
      const recent = await SearchService.getRecentSearches(5, 0);
      setRecentSearches(recent || []);
    } catch (error) {
      console.error('Error loading recent searches:', error);
    }
  };

  const handleItemClick = (type, item) => {
    const query = searchQuery.trim();
    if (query && auth?.isSignedIn) {
      const metadata = (() => {
        switch (type) {
          case 'users':
            return {
              type,
              id: item?.id,
              username: item?.username || null,
              image: item?.avatar_url || item?.avatarUrl || item?.profilePicture || null,
              label: item?.display_name || item?.displayName || item?.username || query,
              sub: item?.username ? `@${item.username}` : null,
            };
          case 'companies':
            return {
              type,
              id: item?.id,
              image: item?.logoUrl || item?.currentAvatar || null,
              label: item?.name || query,
              sub: item?.type || 'Company',
            };
          case 'groups':
            return {
              type,
              id: item?.id,
              image: item?.avatarUrl || null,
              label: item?.name || query,
              sub: item?.memberCount ? `${item.memberCount} members` : 'Group',
            };
          case 'figures':
            return {
              type,
              id: item?.id,
              image: item?.imageUrl || null,
              label: item?.name || query,
              sub: item?.manufacturer || 'Figure',
            };
          case 'posts':
            return {
              type,
              id: item?.id,
              image: item?.media?.length ? item.media[0]?.url : null,
              label: item?.author?.displayName || 'Post',
              sub: item?.caption ? item.caption.substring(0, 50) : '',
            };
          default:
            return { type: 'query' };
        }
      })();

      const queryToSave = metadata?.label || query;
      SearchService.saveSearch(queryToSave, metadata).catch(console.error);
    }

    // Navigate based on type
    switch (type) {
      case 'users':
        navigate(`/user/${item.username}`);
        break;
      case 'companies':
        navigate(`/company/${item.id}`);
        break;
      case 'figures':
        navigate(`/figure/${item.id}`);
        break;
      case 'groups':
        navigate(`/group/${item.id}`);
        break;
      case 'posts':
        navigate(`/post/${item.id}`);
        break;
      case 'tags':
        navigate(`/database?tag=${encodeURIComponent(item?.name || item)}`);
        break;
      case 'origins':
        navigate(`/encyclopedia/origin/${encodeURIComponent(item?.value || item)}`);
        break;
      case 'characters':
        navigate(`/encyclopedia/character/${encodeURIComponent(item?.value || item)}`);
        break;
      case 'classifications':
        navigate(`/encyclopedia/classification/${encodeURIComponent(item?.value || item)}`);
        break;
      default:
        break;
    }
    onClose();
  };

  const handleViewAllResults = () => {
    if (searchQuery.trim()) {
      if (auth?.isSignedIn) {
        SearchService.saveSearch(searchQuery.trim()).catch(console.error);
      }
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      onClose();
    }
  };

  const handleRecentSearchClick = (query) => {
    navigate(`/search?q=${encodeURIComponent(query)}`);
    onClose();
  };

  const handleRecentItemClick = (search) => {
    const meta = search?.metadata || null;
    const type = meta?.type;
    const id = meta?.id;

    if (type && id) {
      switch (type) {
        case 'users':
          if (meta?.username) {
            navigate(`/user/${meta.username}`);
          } else {
            navigate(`/user/${id}`);
          }
          onClose();
          return;
        case 'companies':
          navigate(`/company/${id}`);
          onClose();
          return;
        case 'groups':
          navigate(`/group/${id}`);
          onClose();
          return;
        case 'figures':
          navigate(`/figure/${id}`);
          onClose();
          return;
        case 'posts':
          navigate(`/post/${id}`);
          onClose();
          return;
        default:
          break;
      }
    }

    handleRecentSearchClick(search?.query || '');
  };

  const renderRecentAvatar = (search) => {
    const meta = search?.metadata || null;
    const type = meta?.type;
    const image = meta?.image;

    if (image) {
      const variant = type === 'companies' || type === 'figures' || type === 'posts' ? 'rounded' : 'circular';
      return <Avatar src={image} variant={variant} sx={{ width: 40, height: 40 }} />;
    }

    const icon = (() => {
      switch (type) {
        case 'users':
          return <Person sx={{ fontSize: 20 }} />;
        case 'companies':
          return <Business sx={{ fontSize: 20 }} />;
        case 'groups':
          return <Group sx={{ fontSize: 20 }} />;
        default:
          return <History sx={{ fontSize: 20 }} />;
      }
    })();

    return (
      <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.200', color: 'text.secondary' }}>
        {icon}
      </Avatar>
    );
  };

  const renderUserItem = (user) => (
    <ListItemButton
      key={user.id}
      onClick={() => handleItemClick('users', user)}
      sx={{ py: 1.5 }}
    >
      <ListItemAvatar>
        <Avatar src={user.avatarUrl || user.avatar_url || user.profilePicture || null} sx={{ width: 40, height: 40 }} />
      </ListItemAvatar>
      <ListItemText
        primary={user.displayName || user.display_name || user.username}
        secondary={`@${user.username}`}
        primaryTypographyProps={{ fontWeight: 500 }}
      />
    </ListItemButton>
  );

  const renderCompanyItem = (company) => (
    <ListItemButton
      key={company.id}
      onClick={() => handleItemClick('companies', company)}
      sx={{ py: 1.5 }}
    >
      <ListItemAvatar>
        <Avatar src={company.logoUrl} variant="rounded" sx={{ width: 40, height: 40 }}>
          {company.name?.[0]?.toUpperCase()}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={company.name}
        secondary={company.type || 'Company'}
        primaryTypographyProps={{ fontWeight: 500 }}
      />
    </ListItemButton>
  );

  const renderFigureItem = (figure) => (
    <ListItemButton
      key={figure.id}
      onClick={() => handleItemClick('figures', figure)}
      sx={{ py: 1.5 }}
    >
      <ListItemAvatar>
        <Avatar src={figure.imageUrl} variant="rounded" sx={{ width: 40, height: 40 }}>
          {figure.name?.[0]?.toUpperCase()}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={figure.name}
        secondary={figure.manufacturer || 'Figure'}
        primaryTypographyProps={{ fontWeight: 500 }}
      />
    </ListItemButton>
  );

  const renderGroupItem = (group) => (
    <ListItemButton
      key={group.id}
      onClick={() => handleItemClick('groups', group)}
      sx={{ py: 1.5 }}
    >
      <ListItemAvatar>
        <Avatar src={group.avatarUrl} sx={{ width: 40, height: 40 }}>
          {group.name?.[0]?.toUpperCase()}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={group.name}
        secondary={group.memberCount ? `${group.memberCount} members` : 'Group'}
        primaryTypographyProps={{ fontWeight: 500 }}
      />
    </ListItemButton>
  );

  const renderPostItem = (post) => (
    <ListItemButton
      key={post.id}
      onClick={() => handleItemClick('posts', post)}
      sx={{ py: 1.5 }}
    >
      <ListItemAvatar>
        <Avatar src={post.media.length > 0 ? post.media[0].url : null} variant="rounded" sx={{ width: 40, height: 40 }}>
          <Article sx={{ fontSize: 20 }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={`${post.author?.displayName}`}
        secondary={post.caption?.substring(0, 50) + (post.caption?.length > 50 ? '...' : '')}
        primaryTypographyProps={{ fontWeight: 500 }}
      />
    </ListItemButton>
  );

  const renderSection = (title, items, renderItem, count = null) => {
    if (!items || items.length === 0) return null;

    return (
      <Box key={title}>
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              fontSize: '0.75rem',
            }}
          >
            {title}
          </Typography>
          {typeof count === 'number' ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
              ±{count.toLocaleString()} items
            </Typography>
          ) : null}
        </Box>
        <List disablePadding>
          {items.map(renderItem)}
        </List>
      </Box>
    );
  };

  const counts = results?.meta?.counts || {};
  const tagItems = results?.meta?.tags || [];
  const originItems = results?.meta?.origins || [];
  const characterItems = results?.meta?.characters || [];
  const classificationItems = results?.meta?.classifications || [];

  const hasResults =
    (Array.isArray(tagItems) && tagItems.length > 0) ||
    (Array.isArray(originItems) && originItems.length > 0) ||
    (Array.isArray(characterItems) && characterItems.length > 0) ||
    (Array.isArray(classificationItems) && classificationItems.length > 0) ||
    Object.entries(results)
      .filter(([k]) => k !== 'meta')
      .some(([, arr]) => Array.isArray(arr) && arr.length > 0);

  const renderTagItem = (tag) => (
    <ListItemButton
      key={`${tag?.type || 'tag'}:${tag?.id || tag?.name || tag}`}
      onClick={() => handleItemClick('tags', tag)}
      sx={{ py: 1.25 }}
    >
      <ListItemAvatar>
        <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
          <LocalOffer sx={{ fontSize: 18 }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={tag?.name || String(tag || '')}
        secondary={tag?.type === 'article' ? 'Article tag' : 'Figure tag'}
        primaryTypographyProps={{ fontWeight: 500 }}
      />
    </ListItemButton>
  );

  const renderSimpleFacetItem = (type, iconNode, secondaryLabel) => (val) => (
    <ListItemButton
      key={`${type}:${val?.value || val}`}
      onClick={() => handleItemClick(type, val)}
      sx={{ py: 1.25 }}
    >
      <ListItemAvatar>
        <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.200', color: 'text.secondary' }}>
          {iconNode}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={val?.value || val}
        secondary={secondaryLabel}
        primaryTypographyProps={{ fontWeight: 500 }}
      />
    </ListItemButton>
  );

  const renderCharacterFacetItem = (row) => (
    <ListItemButton
      key={`characters:${row?.value || ''}`}
      onClick={() => handleItemClick('characters', row)}
      sx={{ py: 1.25 }}
    >
      <ListItemAvatar>
        <Avatar src={row?.imageUrl || null} variant="rounded" sx={{ width: 40, height: 40 }}>
          <Person sx={{ fontSize: 20 }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={row?.value}
        secondary={'Characters'}
        primaryTypographyProps={{ fontWeight: 500 }}
      />
    </ListItemButton>
  );

  if (!anchorEl) return null;

  return (
    <Paper
      ref={dropdownRef}
      elevation={8}
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        width: '100%',
        maxHeight: 500,
        overflowY: 'auto',
        overflowX: 'hidden',
        bgcolor: 'white',
        border: '2px solid',
        borderColor: '#64b5f6',
        borderRadius: 1.5,
        mt: 1,
        zIndex: 1300,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : searchQuery.trim().length > 0 ? (
        hasResults ? (
          <>
            {renderSection('Tags', tagItems, renderTagItem, typeof counts.tags === 'number' ? counts.tags : null)}
            {tagItems.length > 0 && (originItems.length > 0 || characterItems.length > 0 || classificationItems.length > 0 || results.users.length > 0 || results.companies.length > 0 || results.figures.length > 0 || results.groups.length > 0 || results.posts.length > 0) && <Divider />}

            {renderSection('Origins', originItems, renderSimpleFacetItem('origins', <Public sx={{ fontSize: 20 }} />, 'Origins'), typeof counts.origins === 'number' ? counts.origins : null)}
            {originItems.length > 0 && (characterItems.length > 0 || classificationItems.length > 0 || results.users.length > 0 || results.companies.length > 0 || results.figures.length > 0 || results.groups.length > 0 || results.posts.length > 0) && <Divider />}

            {renderSection('Characters', characterItems, renderCharacterFacetItem, typeof counts.characters === 'number' ? counts.characters : null)}
            {characterItems.length > 0 && (classificationItems.length > 0 || results.users.length > 0 || results.companies.length > 0 || results.figures.length > 0 || results.groups.length > 0 || results.posts.length > 0) && <Divider />}

            {renderSection('Classifications', classificationItems, renderSimpleFacetItem('classifications', <Category sx={{ fontSize: 20 }} />, 'Classifications'), typeof counts.classifications === 'number' ? counts.classifications : null)}
            {classificationItems.length > 0 && (results.users.length > 0 || results.companies.length > 0 || results.figures.length > 0 || results.groups.length > 0 || results.posts.length > 0) && <Divider />}

            {renderSection('People', results.users, renderUserItem, typeof counts.users === 'number' ? counts.users : null)}
            {results.users.length > 0 && (results.companies.length > 0 || results.figures.length > 0 || results.groups.length > 0 || results.posts.length > 0) && <Divider />}
            
            {renderSection('Companies', results.companies, renderCompanyItem, typeof counts.companies === 'number' ? counts.companies : null)}
            {results.companies.length > 0 && (results.figures.length > 0 || results.groups.length > 0 || results.posts.length > 0) && <Divider />}
            
            {renderSection('Figures', results.figures, renderFigureItem, typeof counts.figures === 'number' ? counts.figures : null)}
            {results.figures.length > 0 && (results.groups.length > 0 || results.posts.length > 0) && <Divider />}
            
            {renderSection('Groups', results.groups, renderGroupItem, typeof counts.groups === 'number' ? counts.groups : null)}
            {results.groups.length > 0 && results.posts.length > 0 && <Divider />}
            
            {renderSection('Posts', results.posts, renderPostItem, typeof counts.posts === 'number' ? counts.posts : null)}

            <Divider />
            <ListItemButton onClick={handleViewAllResults} sx={{ py: 1.5, justifyContent: 'center', bgcolor: 'grey.50' }}>
              <Typography variant="body2" color="primary" fontWeight={600}>
                Show all results
              </Typography>
            </ListItemButton>
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Icon name="search" size={32} style={{ opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No results found for "{searchQuery}"
            </Typography>
          </Box>
        )
      ) : (
        // Recent searches
        recentSearches.length > 0 ? (
          <>
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
              }}
            >
              Recent Searches
            </Typography>
            <List disablePadding>
              {recentSearches.map((search, index) => (
                <ListItemButton
                  key={index}
                  onClick={() => handleRecentItemClick(search)}
                  sx={{ py: 1.5 }}
                >
                  <ListItemAvatar>
                    {renderRecentAvatar(search)}
                  </ListItemAvatar>
                  <ListItemText
                    primary={search?.metadata?.label || search.query}
                    secondary={search?.metadata?.sub || undefined}
                    primaryTypographyProps={{ fontWeight: 500 }}
                  />
                </ListItemButton>
              ))}
            </List>
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Icon name="search" size={32} style={{ opacity: 0.3 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Start typing to search
            </Typography>
          </Box>
        )
      )}
    </Paper>
  );
};

export default SearchDropdown;
